import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Search, ArrowRight, Loader2, Mail, MapPin, Plus, X, DollarSign, TrendingUp, BookOpen, Lightbulb, Trophy, Scale, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "Nationwide", "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
  "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
  "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
  "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const COLORS = ['#0F392B', '#D4AF37', '#2563eb'];
const BG_COLORS = ['bg-[#0F392B]/5', 'bg-[#D4AF37]/5', 'bg-blue-50'];
const TEXT_COLORS = ['text-[#0F392B]', 'text-[#D4AF37]', 'text-blue-700'];
const BORDER_COLORS = ['border-[#0F392B]/20', 'border-[#D4AF37]/20', 'border-blue-200'];

export default function CareerComparePage() {
  const [careers, setCareers] = useState(['', '']);
  const [state, setState] = useState('Nationwide');
  const [email, setEmail] = useState('');
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const updateCareer = (idx, val) => {
    const next = [...careers];
    next[idx] = val;
    setCareers(next);
  };

  const addCareer = () => {
    if (careers.length < 3) setCareers([...careers, '']);
  };

  const removeCareer = (idx) => {
    if (careers.length > 2) setCareers(careers.filter((_, i) => i !== idx));
  };

  const handleCompare = async (e) => {
    e?.preventDefault();
    const filled = careers.filter(c => c.trim());
    if (filled.length < 2) { toast.error('Enter at least 2 careers to compare'); return; }
    if (!emailCaptured && !email.trim()) { toast.error('Enter your email to access comparison'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/career-insights/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ careers: filled, state, email: emailCaptured ? null : email }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Compare failed'); }
      const result = await res.json();
      setData(result);
      if (!emailCaptured && email) setEmailCaptured(true);
    } catch (err) {
      toast.error(err.message || 'Failed to compare careers');
    } finally {
      setLoading(false);
    }
  };

  const maxSalary = data ? Math.max(...(data.careers || []).map(c => c.senior_salary || 0)) : 0;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-16" data-testid="career-compare-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-14 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-xs">Compare Up to 3 Careers</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Compare Careers Side by Side
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed mb-8">
            Not sure which path to choose? Compare salary, growth, skills, and education requirements for 2–3 careers at once.
          </p>

          {/* Compare Form */}
          <form onSubmit={handleCompare} className="max-w-2xl mx-auto" data-testid="compare-form">
            <div className="bg-white rounded-2xl p-5 shadow-2xl text-left">
              <div className="space-y-3">
                {careers.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold"
                      style={{ backgroundColor: COLORS[i] }}>{i + 1}</div>
                    <Input value={c} onChange={e => updateCareer(i, e.target.value)}
                      placeholder={`Career ${i + 1} (e.g., ${['Software Engineer', 'Data Scientist', 'Product Manager'][i]})`}
                      className="h-10 border-[#E2E2E2] text-sm text-foreground flex-1"
                      data-testid={`compare-input-${i}`} />
                    {careers.length > 2 && (
                      <button type="button" onClick={() => removeCareer(i)} className="text-muted-foreground hover:text-red-500 p-1">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                {careers.length < 3 && (
                  <button type="button" onClick={addCareer}
                    className="flex items-center gap-2 text-xs text-[#0F392B] font-medium hover:underline pl-8"
                    data-testid="add-career-btn">
                    <Plus className="h-3.5 w-3.5" /> Add a third career
                  </button>
                )}
              </div>

              <div className="flex gap-3 mt-4 pt-3 border-t border-[#F2F2F0]">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <select value={state} onChange={e => setState(e.target.value)}
                    className="h-10 pl-9 pr-4 border border-[#E2E2E2] rounded-md text-sm bg-white text-foreground appearance-none cursor-pointer w-full"
                    data-testid="compare-state-select">
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {!emailCaptured && (
                <div className="mt-3 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email to access comparison"
                    className="pl-9 h-10 border-[#E2E2E2] text-sm text-foreground" data-testid="compare-email-input" />
                </div>
              )}

              <Button type="submit" disabled={loading}
                className="w-full mt-4 bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 text-sm font-semibold"
                data-testid="compare-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Comparing Careers...</>
                  : <><Scale className="h-4 w-4 mr-2" /> Compare Careers</>}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      {data && data.careers && (
        <section className="max-w-5xl mx-auto px-4 py-12 space-y-8" data-testid="compare-results">
          {/* Salary Comparison */}
          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="salary-comparison">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[#D4AF37]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Salary Comparison</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${data.careers.length}, 1fr)` }}>
                  {data.careers.map((career, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${BORDER_COLORS[i]} ${BG_COLORS[i]}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-xs font-semibold truncate">{career.title}</span>
                      </div>
                      <div className="space-y-2">
                        {[
                          { label: 'Entry', val: career.entry_salary },
                          { label: 'Median', val: career.median_salary },
                          { label: 'Senior', val: career.senior_salary },
                        ].map((s, j) => (
                          <div key={j}>
                            <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
                              <span>{s.label}</span>
                              <span className="font-medium">${(s.val / 1000).toFixed(0)}k</span>
                            </div>
                            <div className="h-3 bg-white rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${(s.val / maxSalary) * 100}%`, backgroundColor: COLORS[i], opacity: 0.6 + j * 0.15 }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Growth & Outlook */}
          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="growth-comparison">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Growth & Job Market</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.careers.length}, 1fr)` }}>
                  {data.careers.map((career, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${BORDER_COLORS[i]}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-xs font-semibold truncate">{career.title}</span>
                      </div>
                      <div className="text-2xl font-bold" style={{ color: COLORS[i] }}>{career.growth_rate}</div>
                      <p className="text-[10px] text-muted-foreground">{career.growth_label}</p>
                      <div className="mt-3 pt-3 border-t border-[#F2F2F0]">
                        <p className="text-xs"><span className="font-medium">{(career.current_jobs || 0).toLocaleString()}</span> <span className="text-muted-foreground">current jobs</span></p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Education Requirements */}
          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="education-comparison">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Education & Training</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.careers.length}, 1fr)` }}>
                  {data.careers.map((career, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${BORDER_COLORS[i]}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-xs font-semibold truncate">{career.title}</span>
                      </div>
                      <Badge className={`${BG_COLORS[i]} ${TEXT_COLORS[i]} border text-[10px] mb-2`}>
                        {career.education_required}
                      </Badge>
                      <p className="text-xs text-muted-foreground mb-3">{career.years_of_training} of training</p>
                      <p className="text-[10px] font-medium text-muted-foreground mb-1">Recommended Majors</p>
                      <div className="flex flex-wrap gap-1">
                        {(career.recommended_majors || []).map((m, j) => (
                          <Badge key={j} variant="outline" className="text-[9px]">{m}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skills Comparison */}
          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="skills-comparison">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-[#D4AF37]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Key Skills Required</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.careers.length}, 1fr)` }}>
                  {data.careers.map((career, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${BORDER_COLORS[i]}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-xs font-semibold truncate">{career.title}</span>
                      </div>
                      <div className="space-y-1.5">
                        {(career.top_skills || []).map((skill, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: COLORS[i], opacity: 1 - j * 0.15 }} />
                            <span className="text-xs">{skill}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quality of Life */}
          <Card className="border-[#E2E2E2] overflow-hidden" data-testid="lifestyle-comparison">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-[#E2E2E2] bg-[#F9F9F7]">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-[#D4AF37]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Work-Life & Satisfaction</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.careers.length}, 1fr)` }}>
                  {data.careers.map((career, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${BORDER_COLORS[i]} text-center`}>
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                        <span className="text-xs font-semibold truncate">{career.title}</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] text-muted-foreground mb-1">Work-Life Balance</p>
                          <div className="flex items-center justify-center gap-0.5">
                            {Array.from({ length: 10 }).map((_, j) => (
                              <div key={j} className="w-2.5 h-5 rounded-sm transition-colors"
                                style={{ backgroundColor: j < (career.work_life_balance || 0) ? COLORS[i] : '#F2F2F0' }} />
                            ))}
                          </div>
                          <p className="text-xs font-bold mt-1">{career.work_life_balance}/10</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground mb-1">Job Satisfaction</p>
                          <div className="flex items-center justify-center gap-0.5">
                            {Array.from({ length: 10 }).map((_, j) => (
                              <div key={j} className="w-2.5 h-5 rounded-sm transition-colors"
                                style={{ backgroundColor: j < (career.job_satisfaction || 0) ? COLORS[i] : '#F2F2F0' }} />
                            ))}
                          </div>
                          <p className="text-xs font-bold mt-1">{career.job_satisfaction}/10</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Best For & Insights */}
          {(data.best_for || data.comparison_insights) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {data.best_for && (
                <Card className="border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]" data-testid="best-for-section">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      <Trophy className="h-4 w-4 text-[#D4AF37]" /> Winner in Each Category
                    </h3>
                    <div className="space-y-2.5">
                      {[
                        { label: 'Highest Salary', value: data.best_for.highest_salary },
                        { label: 'Best Growth Outlook', value: data.best_for.best_growth },
                        { label: 'Best Work-Life Balance', value: data.best_for.best_work_life_balance },
                        { label: 'Fastest Entry', value: data.best_for.fastest_entry },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F2F2F0] last:border-0">
                          <span className="text-xs text-muted-foreground">{item.label}</span>
                          <Badge variant="outline" className="text-[10px] font-medium">{item.value}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {data.comparison_insights && (
                <Card className="border-[#0F392B]/10" data-testid="insights-section">
                  <CardContent className="p-6">
                    <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      <Lightbulb className="h-4 w-4 text-[#0F392B]" /> Key Insights
                    </h3>
                    <div className="space-y-3">
                      {data.comparison_insights.map((insight, i) => (
                        <div key={i} className="flex gap-2.5">
                          <div className="w-5 h-5 rounded-full bg-[#0F392B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-[9px] font-bold text-[#0F392B]">{i + 1}</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* CTA */}
          <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02]">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Found Your Path?
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                Now that you know your direction, let us help you get into the right school. Our portal streamlines your entire college application.
              </p>
              <Link to="/sign-up">
                <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-8 text-sm font-semibold" data-testid="compare-cta-btn">
                  Start Your Application Journey <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Career data powered by AI analysis of labor market trends</p>
        </div>
      </footer>
    </div>
  );
}
